import type { KitchenEvent } from "@shared/types/events";
import { kitchenWsPresence } from "../lib/ws-status";
import { requestFullReload } from "../lib/sync/feed-trigger";

type EventHandler = (event: KitchenEvent) => void;

// Reconnect backoff: 2026-05-24 hotfix kds-ws-stability.
// Prima reconnect era fisso 3s, che spammava il server quando la rete
// scendeva (ogni 3s una nuova connessione fallita). Ora exponential
// backoff capped a 30s.
const RECONNECT_BACKOFF_MIN_MS = 1_000; // primo retry quasi immediato
const RECONNECT_BACKOFF_MAX_MS = 30_000; // cap a 30s
const RECONNECT_BACKOFF_FACTOR = 1.7; // 1s, 1.7s, 2.9s, 4.9s, 8.3s, 14s, 24s, 30s, 30s...

export class KitchenWebSocket {
  private ws: WebSocket | null = null;
  private handlers: Map<string, Set<EventHandler>> = new Map();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectAttempts = 0;
  private url: string;
  // Set to true by disconnect() so that the onclose callback does not
  // schedule a reconnect after an intentional teardown (e.g. React
  // StrictMode unmount or component unmount).
  private destroyed = false;
  // This socket's share of "the display has a live connection", which the
  // order queries read to decide how hard to poll.
  private presence = kitchenWsPresence();

  constructor(token: string) {
    const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    this.url = `${wsProtocol}//${window.location.host}/ws/kitchen?token=${token}`;
  }

  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN) return;

    this.ws = new WebSocket(this.url);

    this.ws.onopen = () => {
      console.log("[KitchenWS] Connected");
      // Reset exponential backoff: connessione riuscita.
      this.reconnectAttempts = 0;
      this.presence.set(true);
      this.startHeartbeat();
      // Events may have been missed while the socket was down: read the whole
      // order list again (feature 334). The sync loop ignores this when a full
      // read has just happened, as it does when the first socket opens.
      requestFullReload();
    };

    this.ws.onmessage = (ev) => {
      try {
        const event: KitchenEvent = JSON.parse(ev.data);
        this.emit(event.type, event);
        this.emit("*", event); // wildcard listener
      } catch {
        console.warn("[KitchenWS] Failed to parse message");
      }
    };

    this.ws.onclose = (ev) => {
      this.stopHeartbeat();
      this.presence.set(false);
      if (this.destroyed) {
        // Intentional disconnect — do not attempt to reconnect.
        console.log("[KitchenWS] Disconnected (intentional)");
        return;
      }
      const delayMs = this.computeBackoffMs();
      console.log(
        `[KitchenWS] Disconnected (code=${ev?.code ?? "n/a"}), reconnecting in ${Math.round(delayMs)}ms (attempt #${this.reconnectAttempts + 1})`,
      );
      this.scheduleReconnect(delayMs);
    };

    this.ws.onerror = (err) => {
      console.error("[KitchenWS] Error", err);
    };
  }

  disconnect(): void {
    // Mark as destroyed first so the onclose callback does not re-schedule
    // a reconnect when the socket reports the closure.
    this.destroyed = true;
    this.stopHeartbeat();
    this.presence.set(false);
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.ws?.close();
    this.ws = null;
  }

  on(eventType: string, handler: EventHandler): () => void {
    if (!this.handlers.has(eventType)) {
      this.handlers.set(eventType, new Set());
    }
    this.handlers.get(eventType)!.add(handler);

    // Return unsubscribe function
    return () => {
      this.handlers.get(eventType)?.delete(handler);
    };
  }

  send(data: Record<string, unknown>): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }

  private emit(eventType: string, event: KitchenEvent): void {
    const handlers = this.handlers.get(eventType);
    if (handlers) {
      for (const handler of handlers) {
        handler(event);
      }
    }
  }

  private startHeartbeat(): void {
    this.heartbeatTimer = setInterval(() => {
      this.send({ type: "heartbeat" });
    }, 25_000);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private scheduleReconnect(delayMs: number): void {
    if (this.reconnectTimer) return;
    this.reconnectAttempts += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delayMs);
  }

  /**
   * Exponential backoff con jitter: cresce di un fattore costante fino al cap.
   * Aggiunge ±20% di jitter per evitare thundering herd se 100 KDS si
   * riconnettono insieme dopo un blip di rete.
   */
  private computeBackoffMs(): number {
    const base = Math.min(
      RECONNECT_BACKOFF_MIN_MS *
        RECONNECT_BACKOFF_FACTOR ** this.reconnectAttempts,
      RECONNECT_BACKOFF_MAX_MS,
    );
    const jitter = base * (0.8 + Math.random() * 0.4); // ±20%
    return jitter;
  }
}
